import { useOne } from '@refinedev/core';
import { FormInstance } from 'antd';
import { SelectProps } from 'antd/lib';
import { useEffect, useMemo, useState } from 'react';

import { Ticket } from '@/model/Ticket/Ticket';

/**
 * Hook que centraliza la lógica de autocompletar la empresa del formulario
 * a partir del ticket seleccionado. Compartido entre useShipmentForm y useReceptionForm.
 *
 * metadata:
 * - feature: forms.ticket-company-autofill
 * - purpose: extrae y unifica la lógica duplicada de ~40 líneas en useShipmentForm y useReceptionForm
 *   que busca la empresa del ticket seleccionado, la agrega a las opciones del select de empresa
 *   y auto-completa el campo company en el formulario
 *
 * @param form - Instancia del formulario Ant Design
 * @param ticketsSelectProps - SelectProps base del selector de tickets (del outlet context)
 * @param companySelectProps - SelectProps base del selector de empresas (ya con initials)
 * @param initialTicketId - ID del ticket inicializado en el formulario
 * @param edit - Si está en modo edición
 * @param onTicketSelected - Callback opcional cuando se selecciona un ticket (para lógica extra como setear cuit)
 */
interface UseTicketCompanyAutoFillOptions {
  form: FormInstance | undefined;
  ticketsSelectProps: SelectProps;
  companySelectProps: SelectProps & { options?: any[] };
  initialTicketId?: number;
  edit?: boolean;
  /** Callback que recibe la opción de empresa y el ticket cuando se autocompleta */
  onTicketSelected?: (
    companyOption: { label: any; value: any; item?: any },
    ticket: Ticket,
  ) => void;
}

export const useTicketCompanyAutoFill = ({
  form,
  ticketsSelectProps,
  companySelectProps,
  initialTicketId,
  edit,
  onTicketSelected,
}: UseTicketCompanyAutoFillOptions) => {
  const [selectedTicketOption, setSelectedTicketOption] = useState<Ticket>();
  const [movilLink, setMovilLink] = useState<string>('');

  // Detectar el ticket inicial desde las opciones cargadas
  useEffect(() => {
    if (initialTicketId) {
      if (ticketsSelectProps.options && ticketsSelectProps.options.length > 0) {
        const index = ticketsSelectProps.options.findIndex(
          option => option.value === initialTicketId,
        );

        if (index !== -1) {
          setSelectedTicketOption(ticketsSelectProps.options[index].item);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialTicketId]);

  // Buscar la empresa del ticket seleccionado
  const { result } = useOne({
    resource: 'empresas',
    id: selectedTicketOption?.empresa,
    queryOptions: {
      enabled: !!selectedTicketOption?.empresa,
    },
  });
  // Armar la opción de empresa del ticket
  const ticketCompanyOption = useMemo(
    () =>
      result
        ? [
            {
              label: result.full_name,
              value: result.id,
              item: result,
            },
          ]
        : [],
    [result],
  );

  // Auto-completar empresa cuando se selecciona ticket (solo en creación)
  useEffect(() => {
    if (selectedTicketOption && ticketCompanyOption.length > 0 && !edit) {
      form?.setFieldValue('company', ticketCompanyOption[0].value);
      setMovilLink(selectedTicketOption?.movil_link || '');
      onTicketSelected?.(ticketCompanyOption[0], selectedTicketOption);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTicketOption, ticketCompanyOption]);

  // Modificar onChange del selector de tickets para capturar la selección
  const ticketsSelectPropsModified: SelectProps = {
    ...ticketsSelectProps,
    onChange: (v, option: any) => {
      ticketsSelectProps?.onChange?.(v, option);
      setSelectedTicketOption(option?.item as Ticket);
    },
  };
  // Combinar opciones de empresa: empresa del ticket primero, luego el resto
  const companySelectPropsModified = useMemo(
    () => ({
      ...companySelectProps,
      options: [
        ...ticketCompanyOption,
        ...(companySelectProps.options?.filter(c => c.value != ticketCompanyOption[0]?.value) ||
          []),
      ],
    }),
    [companySelectProps, ticketCompanyOption],
  );

  return {
    ticketsSelectProps: ticketsSelectPropsModified,
    companySelectProps: companySelectPropsModified,
    ticketCompanyOption,
    movilLink,
    selectedTicketOption,
  };
};
